import React, { useState } from "react";
import { Link } from "react-router-dom";
const heroImage = "/Images/services/oil-gas/drilling-fluids/fluidos_01.webp";

import {
  Droplets,
  FlaskConical,
  ArrowRight,
  Mail,
  Layers3,
  ShieldCheck,
  Zap,
  ShieldAlert,
  Gauge,
  Waves,
  Settings2,
  TrendingDown,
  TrendingUp,
  Clock3,
  RefreshCw,
  Leaf,
  ChevronDown,
} from "lucide-react";

const technologies = [
  {
    title: "G-DRILL DFS",
    subtitle: "Optimización del desempeño de perforación",
    description:
      "Solución especializada orientada a optimizar el comportamiento del sistema de fluidos y mejorar el desempeño operativo durante la perforación.",
    icon: Gauge,
    items: [
      "Optimización de la estabilidad mecánica",
      "Control de propiedades reológicas",
      "Mejora de la tasa de penetración (ROP)",
    ],
  },
  {
    title: "G-DRILL RDF PLUS",
    subtitle: "Estimulación durante la perforación",
    description:
      "Tecnología diseñada para integrar procesos de estimulación dentro de la operación de perforación.",
    icon: Zap,
    items: [
      "Estimulación mientras se perfora",
      "Optimización del desempeño del sistema",
      "Aplicación en operaciones especializadas",
    ],
  },
  {
    title: "G-MAX RDF",
    subtitle: "Sistemas de alta densidad sin barita",
    description:
      "Sistema libre de barita diseñado para aplicaciones que requieren altas densidades y características específicas de desempeño.",
    icon: Droplets,
    items: [
      "Sistema libre de barita",
      "Densidades de hasta 20 ppg",
      "Aplicaciones de perforación especializada",
    ],
  },
  {
    title: "APHRON SYSTEM®",
    subtitle: "Control avanzado de pérdidas",
    description:
      "Solución especializada para el manejo de pérdidas de circulación y operaciones en zonas naturalmente fracturadas.",
    icon: ShieldAlert,
    items: [
      "Control avanzado de pérdidas",
      "Aplicación en zonas fracturadas",
      "Reducción de riesgos operativos",
    ],
  },
  {
    title: "CONTONE®",
    subtitle: "Inhibición y control de torque",
    description:
      "Tecnología orientada a mejorar la inhibición de formaciones y favorecer un desempeño operativo más eficiente.",
    icon: Settings2,
    items: [
      "Inhibición avanzada",
      "Reducción de torque",
      "Mejor control del comportamiento del sistema",
    ],
  },
  {
    title: "WEL SURE",
    subtitle: "Mitigación de pérdidas severas",
    description:
      "Solución especializada para enfrentar condiciones de pérdidas severas de circulación durante las operaciones.",
    icon: Waves,
    items: [
      "Mitigación de pérdidas severas",
      "Aplicación en condiciones exigentes",
      "Apoyo a la continuidad operacional",
    ],
  },
];

const fluidSystems = [
  {
    title: "WBM",
    desc: "Sistemas base agua diseñados para responder a las condiciones y características de las cuencas colombianas.",
    icon: Droplets,
  },
  {
    title: "OBM",
    desc: "Sistemas de alto desempeño para maximizar la estabilidad y eficiencia en formaciones reactivas.",
    icon: FlaskConical,
  },
  {
    title: "OVERBURDEN",
    desc: "Soluciones orientadas a garantizar seguridad y control durante la perforación de las secciones superiores.",
    icon: Layers3,
  },
  {
    title: "DRILL-IN",
    desc: "Sistemas especializados para proteger el yacimiento y preservar sus propiedades durante la perforación.",
    icon: ShieldCheck,
  },
];

const DrillingFluids = () => {
  const [openTechnology, setOpenTechnology] = useState(null);
  return (
    <>
      {/* HERO */}
      <section className="overflow-hidden">
        <div className="relative h-[420px] sm:h-[500px] md:h-[620px] overflow-hidden">
          <img
            src={heroImage}
            alt="Consultoría Ambiental"
            className="w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-black/50" />

          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-transparent" />

          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ee7d32]/15 border border-[#ee7d32]/30 text-[#ee7d32] text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] backdrop-blur-sm">
                  Servicios
                </span>

                <h1 className="mt-6 text-4xl sm:text-5xl md:text-6xl xl:text-7xl font-bold text-white leading-[0.95]">
                  Fluidos de <br />
                  <span className="text-[#ee7d32]">Perforación</span>
                </h1>

                <p className="hidden md:block mt-6 text-white/80 text-sm sm:text-base md:text-lg leading-7 max-w-xl">
                  Diseñamos, formulamos y ejecutamos sistemas de fluidos de
                  perforación a la medida para maximizar la estabilidad del
                  pozo, optimizar el ROP y reducir los riesgos operacionales.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  {[
                    "+1.300 pozos asistidos",
                    "WBM / OBM / Drill-In",
                    "Cuencas de Colombia",
                    "Soporte técnico",
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="px-4 py-2 rounded-full bg-white/10 border border-white/10 backdrop-blur-sm text-white/90 text-sm"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUE HACEMOS */}
      <section className="relative py-16 md:py-24 bg-[#f3f3f3] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="relative">
              <div
                className="
            absolute
            -bottom-5
            -right-5
            w-24
            h-24
            rounded-full
            bg-[#ee7d32]/10
            z-0
          "
              />

              <div
                className="
            relative
            z-10
            overflow-hidden
            rounded-[38px]
            rounded-tr-[110px]
            rounded-bl-[110px]
            shadow-[0_20px_60px_rgba(0,0,0,0.10)]
          "
              >
                <img
                  src="/Images/services/oil-gas/drilling-fluids/fluidos_02.webp"
                  alt="Diseño y optimización de fluidos de perforación"
                  className="
              w-full
              h-[340px]
              sm:h-[430px]
              md:h-[520px]
              object-cover
              transition-transform
              duration-700
              hover:scale-105
            "
                />
              </div>
            </div>

            <div>
              <div className="w-14 h-[4px] bg-[#ee7d32] rounded-full mb-5" />

              <span
                className="
            text-xs
            sm:text-sm
            font-bold
            uppercase
            tracking-[0.16em]
            text-[#ee7d32]
          "
              >
                Optimización de perforación
              </span>

              <h2
                className="
            mt-5
            text-3xl
            sm:text-4xl
            md:text-5xl
            font-bold
            text-[#111]
            leading-tight
          "
              >
                Diseño de fluidos para{" "}
                <span className="text-[#ee7d32]">máximo rendimiento</span>
              </h2>

              <p
                className="
            mt-6
            text-[#555]
            text-sm
            sm:text-base
            md:text-lg
            leading-7
            md:leading-8
            max-w-xl
          "
              >
                Cada sistema de fluidos se diseña a partir del análisis
                geológico y las condiciones reales del pozo, buscando
                estabilidad, eficiencia operativa y control del yacimiento.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <span
                  className="
              inline-flex
              items-center
              rounded-full
              bg-[#fff3eb]
              px-4
              py-2
              text-sm
              font-semibold
              text-[#ee7d32]
            "
                >
                  Optimización de ROP
                </span>

                <span
                  className="
              inline-flex
              items-center
              rounded-full
              bg-[#fff3eb]
              px-4
              py-2
              text-sm
              font-semibold
              text-[#ee7d32]
            "
                >
                  Control de pérdidas
                </span>

                <span
                  className="
              inline-flex
              items-center
              rounded-full
              bg-[#fff3eb]
              px-4
              py-2
              text-sm
              font-semibold
              text-[#ee7d32]
            "
                >
                  Estabilidad del pozo
                </span>

                <span
                  className="
              inline-flex
              items-center
              rounded-full
              bg-[#fff3eb]
              px-4
              py-2
              text-sm
              font-semibold
              text-[#ee7d32]
            "
                >
                  Protección del yacimiento
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SISTEMAS DE FLUIDOS */}
      <section className="relative py-20 md:py-24 bg-white overflow-hidden">
        {/* DECORACIÓN DE FONDO */}
        <div className="absolute top-0 right-0 w-[420px] h-[420px] bg-[#ee7d32]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[350px] h-[350px] bg-[#ee7d32]/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* ENCABEZADO */}
          <div className="max-w-3xl">
            <div className="w-14 h-[4px] bg-[#ee7d32] rounded-full mb-5" />

            <h2 className="mt-6 text-4xl sm:text-5xl md:text-6xl font-bold text-[#111] leading-[1]">
              Sistemas de <span className="text-[#ee7d32]">Fluidos</span>
            </h2>

            <p className="mt-6 text-base sm:text-lg text-[#555] leading-8 max-w-2xl">
              Diseñamos y seleccionamos sistemas de fluidos especializados de
              acuerdo con las características de cada formación y las
              necesidades operativas del pozo, buscando seguridad, eficiencia y
              protección del yacimiento.
            </p>
          </div>

          {/* TARJETAS */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mt-16 py-8">
            {fluidSystems.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="
              group
              relative
              bg-white
              rounded-3xl
              p-8
              pt-14
              text-center
              border
              border-gray-100
              shadow-[0_20px_60px_rgba(0,0,0,0.06)]
              hover:shadow-[0_30px_80px_rgba(238,125,50,0.18)]
              transition-all
              duration-500
              hover:-translate-y-2
            "
                >
                  {/* ICONO */}
                  <div
                    className="
                absolute
                -top-10
                left-1/2
                -translate-x-1/2
                w-20
                h-20
                bg-[#ee7d32]
                rounded-full
                flex
                items-center
                justify-center
                shadow-xl
                group-hover:scale-110
                transition-transform
                duration-300
              "
                  >
                    <Icon
                      className="w-8 h-8"
                      stroke="#ffffff"
                      strokeWidth={2.2}
                    />
                  </div>

                  {/* NÚMERO */}
                  <span className="block text-[11px] font-bold tracking-[0.2em] text-[#ee7d32] mb-2">
                    SISTEMA 0{index + 1}
                  </span>

                  {/* TÍTULO */}
                  <h3 className="text-[20px] font-[800] text-[#111]">
                    {item.title}
                  </h3>

                  {/* DESCRIPCIÓN */}
                  <p className="text-sm text-gray-600 leading-[1.7] mt-4">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TECNOLOGÍAS */}
      <section className="py-20 md:py-28 bg-[#f3f3f3] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="w-14 h-1 bg-[#ee7d32] rounded-full mb-6" />

            <h2 className="text-4xl md:text-5xl font-bold text-[#111] leading-tight">
              Tecnologías que
              <span className="text-[#ee7d32]"> optimizan la perforación</span>
            </h2>

            <p className="mt-5 text-gray-600 text-lg leading-8">
              Integramos tecnologías y soluciones especializadas para responder
              a los diferentes desafíos de perforación, optimizando el desempeño
              de los sistemas de fluidos y contribuyendo a operaciones más
              eficientes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-7 mt-16">
            {technologies.map((item, index) => {
              const Icon = item.icon;
              const isOpen = openTechnology === index;

              return (
                <div
                  key={index}
                  className="
          group
          relative
          flex
          flex-col
          h-full
          bg-white
          rounded-3xl
          px-7
          pt-14
          pb-8
          border
          border-gray-100
          shadow-[0_20px_60px_rgba(0,0,0,0.06)]
          transition-all
          duration-500
          hover:-translate-y-2
          hover:shadow-[0_30px_80px_rgba(238,125,50,0.16)]
        "
                >
                  <div
                    className="
            absolute
            -top-9
            left-7
            w-[72px]
            h-[72px]
            rounded-full
            bg-[#ee7d32]
            flex
            items-center
            justify-center
            shadow-[0_12px_30px_rgba(238,125,50,0.25)]
            transition-transform
            duration-300
            group-hover:scale-110
          "
                  >
                    <Icon className="w-8 h-8" stroke="white" strokeWidth={2} />
                  </div>

                  <div className="flex flex-col flex-1">
                    <h3
                      className="
              text-xl
              sm:text-[22px]
              font-extrabold
              text-[#111]
              leading-tight
              pr-10
            "
                    >
                      {item.title}
                    </h3>

                    <p
                      className="
              mt-2
              text-sm
              font-semibold
              text-[#ee7d32]
            "
                    >
                      {item.subtitle}
                    </p>

                    <p
                      className="
              mt-5
              text-sm
              text-gray-600
              leading-7
            "
                    >
                      {item.description}
                    </p>

                    <div
                      className={`
              flex
              justify-end
              pt-6
              transition-all
              duration-300
              ${isOpen ? "mb-6" : "mt-auto"}
            `}
                    >
                      <button
                        type="button"
                        onClick={() => setOpenTechnology(isOpen ? null : index)}
                        className="
                group/details
                inline-flex
                items-center
                gap-2
                text-sm
                font-bold
                transition-all
                duration-300
              "
                      >
                        <span
                          className="
                  text-[#666666]
                  group-hover/details:text-[#ee7d32]
                  transition-colors
                  duration-300
                "
                        >
                          {isOpen ? "Ocultar detalles" : "Conocer más"}
                        </span>

                        <ChevronDown
                          className={`
                  w-4
                  h-4
                  stroke-[#666666]
                  group-hover/details:stroke-[#ee7d32]
                  transition-all
                  duration-300
                  ${isOpen ? "rotate-180 stroke-[#ee7d32]" : ""}
                `}
                          strokeWidth={2}
                        />
                      </button>
                    </div>

                    <div
                      className={`
              overflow-hidden
              transition-all
              duration-500
              ease-in-out
              ${isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}
            `}
                    >
                      <div className="border-t border-gray-100 pt-6">
                        <p
                          className="
                  mb-5
                  text-[10px]
                  font-bold
                  uppercase
                  tracking-[0.16em]
                  text-gray-400
                "
                        >
                          Aplicaciones y beneficios
                        </p>

                        <div className="space-y-3">
                          {item.items.map((x, i) => (
                            <div key={i} className="flex items-start gap-3">
                              <div
                                className="
                        mt-1
                        w-5
                        h-5
                        rounded-full
                        bg-[#ee7d32]/10
                        flex
                        items-center
                        justify-center
                        shrink-0
                      "
                              >
                                <svg
                                  viewBox="0 0 20 20"
                                  fill="none"
                                  className="w-3 h-3"
                                >
                                  <path
                                    d="M5 10.5L8.5 14L15 6.5"
                                    stroke="#ee7d32"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>

                              <span
                                className="
                        text-sm
                        text-gray-600
                        leading-6
                      "
                              >
                                {x}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LABORATORIO QA / QC */}
      <section className="relative py-16 md:py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="relative">
              <div
                className="
            absolute
            -bottom-5
            -right-5
            w-24
            h-24
            rounded-full
            bg-[#ee7d32]/10
            z-0
          "
              />

              <div
                className="
            absolute
            -top-5
            -left-5
            w-16
            h-16
            rounded-full
            border
            border-[#ee7d32]/20
            z-0
          "
              />

              <div
                className="
            relative
            z-10
            overflow-hidden
            rounded-[38px]
            rounded-tr-[110px]
            rounded-bl-[110px]
            shadow-[0_20px_60px_rgba(0,0,0,0.10)]
          "
              >
                <img
                  src="/Images/services/oil-gas/drilling-fluids/laboratory.webp"
                  alt="Laboratorio QA/QC para control y optimización de fluidos de perforación"
                  className="
              w-full
              h-[340px]
              sm:h-[430px]
              md:h-[520px]
              object-cover
              transition-transform
              duration-700
              hover:scale-105
            "
                />

                <div
                  className="
              absolute
              inset-0
              bg-gradient-to-t
              from-black/30
              via-transparent
              to-transparent
              pointer-events-none
            "
                />
              </div>
            </div>

            <div>
              <div className="w-14 h-[4px] bg-[#ee7d32] rounded-full mb-5" />

              <span
                className="
            text-xs
            sm:text-sm
            font-bold
            uppercase
            tracking-[0.16em]
            text-[#ee7d32]
          "
              >
                Control y calidad
              </span>

              <h2
                className="
            mt-5
            text-3xl
            sm:text-4xl
            md:text-5xl
            font-bold
            text-[#111]
            leading-tight
          "
              >
                Laboratorio <span className="text-[#ee7d32]">QA/QC</span>
              </h2>

              <h3
                className="
            mt-4
            text-xl
            sm:text-2xl
            font-semibold
            text-[#222]
            leading-snug
          "
              >
                Control y optimización de fluidos de perforación
              </h3>

              <p
                className="
            mt-5
            text-[#555]
            text-sm
            sm:text-base
            md:text-lg
            leading-7
            md:leading-8
            max-w-xl
          "
              >
                Nuestro laboratorio QA/QC evalúa continuamente el desempeño de
                los fluidos para garantizar estabilidad del pozo, eficiencia
                operativa y control de las condiciones críticas durante la
                perforación.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {[
                  "Inhibición",
                  "Lubricidad",
                  "Control de filtrado",
                  "Estabilidad del pozo",
                ].map((item, index) => (
                  <div
                    key={index}
                    className="
                flex
                items-center
                gap-3
                py-2
              "
                  >
                    <div
                      className="
    relative
    w-6
    h-6
    rounded-full
    flex
    items-center
    justify-center
    shrink-0
  "
                    >
                      <span
                        className="
      absolute
      w-3
      h-3
      rounded-full
      bg-[#ee7d32]/30
      animate-ping
    "
                      />

                      <span
                        className="
      relative
      w-2
      h-2
      rounded-full
      bg-[#ee7d32]
    "
                      />
                    </div>

                    <span
                      className="
                  text-sm
                  sm:text-base
                  font-semibold
                  text-[#444]
                "
                    >
                      {item}
                    </span>
                  </div>
                ))}
              </div>

              <div
                className="
            mt-9
            pt-7
            border-t
            border-gray-100
          "
              >
                <div className="flex items-start gap-4">
                  <div
                    className="
    w-12
    h-12
    rounded-2xl
    bg-[#ee7d32]
    flex
    items-center
    justify-center
    shrink-0
    shadow-[0_10px_25px_rgba(238,125,50,0.20)]
  "
                  >
                    <TrendingDown
                      className="w-6 h-6"
                      stroke="#ffffff"
                      strokeWidth={2}
                    />
                  </div>

                  <div>
                    <h4
                      className="
                  text-xl
                  sm:text-2xl
                  font-bold
                  text-[#111]
                "
                    >
                      Menor NPT,
                      <span className="text-[#ee7d32]">
                        {" "}
                        mayor eficiencia operativa
                      </span>
                    </h4>

                    <p
                      className="
                  mt-2
                  text-sm
                  text-[#666]
                  leading-6
                  max-w-xl
                "
                    >
                      Nuestros sistemas optimizan las operaciones de
                      perforación, reduciendo el Non-Productive Time (NPT), el
                      riesgo operacional y los costos.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFICIOS*/}
      <section className="relative py-20 md:py-28 bg-[#f3f3f3] overflow-hidden">
        <div
          className="
      absolute
      -top-32
      -right-32
      w-[420px]
      h-[420px]
      rounded-full
      bg-[#ee7d32]/5
      blur-3xl
      pointer-events-none
    "
        />

        <div
          className="
      absolute
      -bottom-40
      -left-40
      w-[450px]
      h-[450px]
      rounded-full
      bg-[#ee7d32]/5
      blur-3xl
      pointer-events-none
    "
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <div className="w-14 h-1 bg-[#ee7d32] rounded-full mb-6" />

            <span
              className="
          text-xs
          sm:text-sm
          font-bold
          uppercase
          tracking-[0.16em]
          text-[#ee7d32]
        "
            >
              Valor para su operación
            </span>

            <h2
              className="
          mt-4
          text-4xl
          md:text-5xl
          font-bold
          text-[#111]
          leading-tight
        "
            >
              Resultados que
              <span className="text-[#ee7d32]"> impactan su operación</span>
            </h2>

            <p
              className="
          mt-5
          text-gray-600
          text-base
          md:text-lg
          leading-8
          max-w-2xl
        "
            >
              Nuestras soluciones están orientadas a generar beneficios medibles
              durante la operación, combinando desempeño técnico, eficiencia de
              recursos y sostenibilidad.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
            <div
              className="
          group
          relative
          bg-white
          rounded-[28px]
          p-7
          sm:p-8
          border
          border-gray-100
          shadow-[0_15px_45px_rgba(0,0,0,0.04)]
          transition-all
          duration-500
          hover:-translate-y-2
          hover:shadow-[0_25px_60px_rgba(238,125,50,0.12)]
          overflow-hidden
        "
            >
              <span
                className="
            absolute
            top-6
            right-7
            text-5xl
            font-black
            text-[#f7f7f7]
            transition-colors
            duration-300
            group-hover:text-[#fff3eb]
          "
              >
                01
              </span>

              <div className="relative z-10">
                <div
                  className="
              w-14
              h-14
              rounded-2xl
              bg-[#fff3eb]
              flex
              items-center
              justify-center
              mb-6
              transition-all
              duration-300
              group-hover:bg-[#ee7d32]
              group-hover:shadow-[0_10px_25px_rgba(238,125,50,0.20)]
            "
                >
                  <TrendingUp
                    className="
                w-7
                h-7
                transition-colors
                duration-300
                group-hover:stroke-white
              "
                    stroke="#ee7d32"
                    strokeWidth={1.8}
                  />
                </div>

                <h3 className="text-xl sm:text-2xl font-bold text-[#111]">
                  Mayor productividad
                </h3>

                <p className="mt-4 text-sm text-gray-600 leading-7 max-w-xl">
                  Sistemas Drill-In diseñados para minimizar el daño de
                  formación y maximizar la movilidad de hidrocarburos.
                </p>
              </div>
            </div>

            <div
              className="
          group
          relative
          bg-white
          rounded-[28px]
          p-7
          sm:p-8
          border
          border-gray-100
          shadow-[0_15px_45px_rgba(0,0,0,0.04)]
          transition-all
          duration-500
          hover:-translate-y-2
          hover:shadow-[0_25px_60px_rgba(238,125,50,0.12)]
          overflow-hidden
        "
            >
              <span
                className="
            absolute
            top-6
            right-7
            text-5xl
            font-black
            text-[#f7f7f7]
            transition-colors
            duration-300
            group-hover:text-[#fff3eb]
          "
              >
                02
              </span>

              <div className="relative z-10">
                <div
                  className="
              w-14
              h-14
              rounded-2xl
              bg-[#fff3eb]
              flex
              items-center
              justify-center
              mb-6
              transition-all
              duration-300
              group-hover:bg-[#ee7d32]
              group-hover:shadow-[0_10px_25px_rgba(238,125,50,0.20)]
            "
                >
                  <Clock3
                    className="
                w-7
                h-7
                transition-colors
                duration-300
                group-hover:stroke-white
              "
                    stroke="#ee7d32"
                    strokeWidth={1.8}
                  />
                </div>

                <h3 className="text-xl sm:text-2xl font-bold text-[#111]">
                  Menor tiempo no productivo
                </h3>

                <p className="mt-4 text-sm text-gray-600 leading-7 max-w-xl">
                  Control de estabilidad, lubricidad y filtrado para reducir
                  eventos operacionales y mantener la continuidad de la
                  operación.
                </p>
              </div>
            </div>

            <div
              className="
          group
          relative
          bg-white
          rounded-[28px]
          p-7
          sm:p-8
          border
          border-gray-100
          shadow-[0_15px_45px_rgba(0,0,0,0.04)]
          transition-all
          duration-500
          hover:-translate-y-2
          hover:shadow-[0_25px_60px_rgba(238,125,50,0.12)]
          overflow-hidden
        "
            >
              <span
                className="
            absolute
            top-6
            right-7
            text-5xl
            font-black
            text-[#f7f7f7]
            transition-colors
            duration-300
            group-hover:text-[#fff3eb]
          "
              >
                03
              </span>

              <div className="relative z-10">
                <div
                  className="
              w-14
              h-14
              rounded-2xl
              bg-[#fff3eb]
              flex
              items-center
              justify-center
              mb-6
              transition-all
              duration-300
              group-hover:bg-[#ee7d32]
              group-hover:shadow-[0_10px_25px_rgba(238,125,50,0.20)]
            "
                >
                  <RefreshCw
                    className="
                w-7
                h-7
                transition-colors
                duration-300
                group-hover:stroke-white
              "
                    stroke="#ee7d32"
                    strokeWidth={1.8}
                  />
                </div>

                <h3 className="text-xl sm:text-2xl font-bold text-[#111]">
                  Reutilización de fluidos
                </h3>

                <p className="mt-4 text-sm text-gray-600 leading-7 max-w-xl">
                  Tecnologías que reducen las diluciones y favorecen un uso más
                  eficiente de los recursos durante las operaciones.
                </p>
              </div>
            </div>

            <div
              className="
          group
          relative
          bg-white
          rounded-[28px]
          p-7
          sm:p-8
          border
          border-gray-100
          shadow-[0_15px_45px_rgba(0,0,0,0.04)]
          transition-all
          duration-500
          hover:-translate-y-2
          hover:shadow-[0_25px_60px_rgba(238,125,50,0.12)]
          overflow-hidden
        "
            >
              <span
                className="
            absolute
            top-6
            right-7
            text-5xl
            font-black
            text-[#f7f7f7]
            transition-colors
            duration-300
            group-hover:text-[#fff3eb]
          "
              >
                04
              </span>

              <div className="relative z-10">
                <div
                  className="
              w-14
              h-14
              rounded-2xl
              bg-[#fff3eb]
              flex
              items-center
              justify-center
              mb-6
              transition-all
              duration-300
              group-hover:bg-[#ee7d32]
              group-hover:shadow-[0_10px_25px_rgba(238,125,50,0.20)]
            "
                >
                  <Leaf
                    className="
                w-7
                h-7
                transition-colors
                duration-300
                group-hover:stroke-white
              "
                    stroke="#ee7d32"
                    strokeWidth={1.8}
                  />
                </div>

                <h3 className="text-xl sm:text-2xl font-bold text-[#111]">
                  Menor impacto ambiental
                </h3>

                <p className="mt-4 text-sm text-gray-600 leading-7 max-w-xl">
                  Uso de productos biodegradables y estrategias enfocadas en la
                  reducción de la huella hídrica y el impacto ambiental.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACTO CTA */}
      <section className="relative py-14 md:py-20 bg-white overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="group relative overflow-hidden rounded-[28px] md:rounded-[30px] border border-[#ececec] bg-white shadow-[0_10px_35px_rgba(0,0,0,0.04)] transition-all duration-300 hover:shadow-[0_18px_50px_rgba(0,0,0,0.08)]">
            <div className="absolute top-0 left-0 h-full w-1.5 bg-gradient-to-b from-[#ee7d32] via-orange-400 to-[#ee7d32]" />

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 px-5 py-7 sm:px-6 md:px-8 md:py-8 lg:px-10 lg:py-10">
              <div className="flex flex-col sm:flex-row items-start gap-5 max-w-3xl">
                <div className="relative flex-shrink-0">
                  <div className="absolute inset-0 bg-[#ee7d32]/20 blur-xl rounded-2xl" />

                  <div className="relative w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center">
                    <Mail
                      size={34}
                      className="md:w-[42px] md:h-[42px] transition-colors duration-300"
                      stroke="#ee7d32"
                    />
                  </div>
                </div>

                <div className="min-w-0">
                  <h3 className="mt-3 text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-[#111827] leading-tight">
                    ¿Necesitas Soluciones en Fluidos de Perforación
                    personalizadas y de alta calidad?
                  </h3>

                  <p className="hidden md:block mt-3 text-sm md:text-base leading-6 md:leading-7 text-[#6b7280] max-w-2xl">
                    Sistemas WBM, OBM y Drill-In diseñados para aumentar la
                    productividad, mejorar la estabilidad del pozo y reducir
                    riesgos operacionales.
                  </p>
                </div>
              </div>

              <div className="flex w-full lg:w-auto">
                <Link
                  to="/contact-us?division=oilgas&service=Fluidos de perforación"
                  className="group/btn inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-lg bg-[#ee7d32] px-6 py-3.5 md:px-7 md:py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#e56f20] hover:shadow-lg hover:shadow-[#ee7d32]/20"
                >
                  Solicitar Asesoría
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover/btn:translate-x-1"
                    color="white"
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default DrillingFluids;
